import { useEffect, useState } from "react";
import API from "@/lib/api";
import { MapPin, CheckCircle2, AlertTriangle, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Personalised shipping pre-flight check for anonymous landing-page visitors.
 *
 * - Pulls /geo/state (IP-based lookup) to detect the visitor's state.
 * - Cross-references against /pharmacies/shipping/coverage to decide if any
 *   of our partner pharmacies ship there.
 * - When covered: small emerald confirmation pill.
 * - When not covered: inline waitlist signup so the visitor doesn't bounce.
 * - Returns null while loading or when the geo lookup is inconclusive
 *   (e.g. corporate VPN / IPv6 unrouted) so we never show a misleading
 *   "we don't ship there" message for an unknown location.
 */
export default function GeoShippingTeaser() {
  const [state, setState] = useState(null);
  const [states, setStates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [geo, cov] = await Promise.all([
          API.get("/geo/state"),
          API.get("/pharmacies/shipping/coverage"),
        ]);
        if (cancelled) return;
        setState(geo.data?.state || null);
        setStates(cov.data?.states || []);
      } catch {
        /* leave null — component will render nothing */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !state || !Array.isArray(states)) return null;

  const covered = states.includes(state);

  if (covered) {
    return (
      <div
        className="mx-1 mb-2 px-3 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center gap-2.5"
        data-testid="hamburger-geo-covered"
      >
        <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
          <MapPin className="w-3.5 h-3.5 text-emerald-700" />
        </div>
        <div className="flex-1 min-w-0 leading-tight">
          <p className="text-[11px] uppercase tracking-wider text-emerald-700/80 font-semibold">Detected · {state}</p>
          <p className="text-sm font-semibold text-emerald-800 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> We ship to your state
          </p>
        </div>
      </div>
    );
  }

  // Not covered → waitlist form
  if (joined) {
    return (
      <div
        className="mx-1 mb-2 px-3 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200"
        data-testid="hamburger-geo-waitlist-joined"
      >
        <p className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4" /> You&apos;re on the list for {state}
        </p>
        <p className="text-[11px] text-emerald-700/80 mt-0.5">We&apos;ll email you the moment {state} comes online.</p>
      </div>
    );
  }

  return (
    <div
      className="mx-1 mb-2 px-3 py-3 rounded-xl bg-rose-50 border border-rose-200"
      data-testid="hamburger-geo-not-covered"
    >
      <div className="flex items-start gap-2.5 mb-2">
        <div className="w-7 h-7 rounded-lg bg-rose-100 flex items-center justify-center shrink-0">
          <AlertTriangle className="w-3.5 h-3.5 text-rose-700" />
        </div>
        <div className="flex-1 min-w-0 leading-tight">
          <p className="text-[11px] uppercase tracking-wider text-rose-700/80 font-semibold">Detected · {state}</p>
          <p className="text-sm font-semibold text-rose-800">Not yet shipping to {state}</p>
          <p className="text-[11px] text-rose-700/80 mt-0.5">Get notified the moment we expand:</p>
        </div>
      </div>
      <form
        className="flex items-center gap-1.5"
        onSubmit={async (e) => {
          e.preventDefault();
          const v = (email || "").trim().toLowerCase();
          if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { toast.error("Enter a valid email"); return; }
          setSubmitting(true);
          try {
            await API.post("/pharmacies/shipping/waitlist", { email: v, state, source: "landing-hamburger" });
            setJoined(true);
            toast.success(`We'll email you when ${state} comes online.`);
          } catch {
            toast.error("Couldn't add you — try again.");
          } finally { setSubmitting(false); }
        }}
        data-testid="hamburger-geo-waitlist-form"
      >
        <div className="flex-1 flex items-center gap-1.5 bg-white border border-rose-200 rounded-lg px-2 min-w-0">
          <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="flex-1 text-xs bg-transparent py-1.5 focus:outline-none min-w-0"
            data-testid="hamburger-geo-waitlist-email"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50 shrink-0"
          data-testid="hamburger-geo-waitlist-submit"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Notify"}
        </button>
      </form>
    </div>
  );
}
